// src/pages/HandmadeGifts.js
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/HandmadeGifts.css";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

// Default page copy (used if Firestore is missing or empty)
const DEFAULT_PAGE = {
  heroTitle: "Crafted with Heart.\nWrapped in Thought.",
  heroSubtitle:
    "Turning Moments into Keepsakes – Custom handmade gifts that speak your story, made with love, details, and a dash of SuShe magic.",
  // If heroBackground is empty, no image (just CSS fallback color)
  heroBackground: "",
  hasWorkshop: false,
  workshopTitle: "Workshop",
  workshopDescription: "Something exciting is in the works... Stay tuned!",
  workshopCtaText: "Craft With Me",
  workshopCtaUrl: "",
  workshopImage: "",
};

const HandmadeGifts = () => {
  // Page content (hero + workshop block)
  const [page, setPage] = useState(DEFAULT_PAGE);

  // Gift cards (from Firestore)
  const [gifts, setGifts] = useState([]); // [{title,imageUrl,description,price,buyUrl,order,active}]
  const [loading, setLoading] = useState(true);

  // Horizontal scroller + arrows
  const cardScrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);

  // Load Firestore data
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // 1) Page content
        const pageRef = doc(db, "pageContent", "handmadeGifts");
        const pageSnap = await getDoc(pageRef);

        if (mounted && pageSnap.exists()) {
          const d = pageSnap.data() || {};
          setPage((prev) => ({
            ...prev,
            heroTitle: d.heroTitle || prev.heroTitle,
            heroSubtitle: d.heroSubtitle || prev.heroSubtitle,
            // Keep empty string if not provided (no image shown)
            heroBackground: d.heroBackground || "",
            hasWorkshop: Boolean(d.hasWorkshop),
            workshopTitle: d.workshopTitle || prev.workshopTitle,
            workshopDescription: d.workshopDescription || prev.workshopDescription,
            workshopCtaText: d.workshopCtaText || prev.workshopCtaText,
            workshopCtaUrl: d.workshopCtaUrl || prev.workshopCtaUrl,
            workshopImage: d.workshopImage || prev.workshopImage,
          }));
        }

        // 2) Gifts list
        const snap = await getDocs(collection(db, "handmadeGifts"));
        const items = [];
        snap.forEach((docu) => {
          const data = docu.data() || {};
          items.push({
            id: docu.id,
            title: data.title || "Untitled",
            imageUrl: data.imageUrl || "",
            description: data.description || "",
            price: data.price || "",
            buyUrl: data.buyUrl || "",
            order: typeof data.order === "number" ? data.order : 9999,
            active: data.active !== false, // default true
          });
        });

        const sorted = items.filter((g) => g.active).sort((a, b) => a.order - b.order);
        if (mounted) setGifts(sorted);
      } catch (err) {
        console.error("HandmadeGifts: failed to load data:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Decide when to show arrows (only if there are items AND overflow)
  useEffect(() => {
    const el = cardScrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 4;
      setShowArrows(gifts.length > 0 && hasOverflow);
    };

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    const t = setTimeout(checkOverflow, 50);

    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [gifts]);

  const scrollCards = (direction) => {
    const el = cardScrollRef.current;
    if (!el) return;
    const scrollAmount = Math.min(360, Math.max(240, el.clientWidth * 0.85));
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Only apply a background image if the Firestore field is present
  const bgStyle = page.heroBackground ? { backgroundImage: `url(${page.heroBackground})` } : {};

  const lines = String(page.heroTitle || "").split("\n");
  const hasCta = page.hasWorkshop && !!page.workshopCtaUrl;

  return (
    <div className="handmade-container">
      {/* Hero Section */}
      <div className="handmade-hero" style={bgStyle}>
        <div className="handmade-hero-overlay" />
        <div className="handmade-hero-text">
          <h1>
            {lines.map((ln, i) => (
              <React.Fragment key={i}>
                {ln}
                {i < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p>{page.heroSubtitle}</p>
        </div>
      </div>

      {/* Handmade With Love Section */}
      <div className="handmade-section">
        <h2 className="handmade-heading">Handmade With Love</h2>

        <div className="handmade-cards-wrapper">
          {showArrows && (
            <button
              type="button"
              className="scroll-arrow left"
              onClick={() => scrollCards("left")}
              aria-label="Scroll gifts left"
            >
              ‹
            </button>
          )}

          <div className="handmade-cards" ref={cardScrollRef}>
            {loading ? (
              <div className="no-handmade-message">Loading gifts…</div>
            ) : gifts.length > 0 ? (
              gifts.map((gift) => {
                const imgStyle = gift.imageUrl ? { backgroundImage: `url(${gift.imageUrl})` } : {};
                return (
                  <div key={gift.id} className="handmade-card">
                    <div
                      className={`gift-photo ${gift.imageUrl ? "" : "empty"}`}
                      style={imgStyle}
                      role="img"
                      aria-label={gift.imageUrl ? gift.title : "No image"}
                    />
                    <div className="gift-details">
                      <h3>{gift.title}</h3>
                      {gift.description && <p>{gift.description}</p>}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-handmade-message">
                ✂️ No handmade magic added here yet – stay tuned!
              </div>
            )}
          </div>

        {showArrows && (
            <button
              type="button"
              className="scroll-arrow right"
              onClick={() => scrollCards("right")}
              aria-label="Scroll gifts right"
            >
              ›
            </button>
          )}
        </div>

        <Link to="/contact" className="enquire-button">
          Enquire
        </Link>
      </div>

      {/* Section Divider */}
      <div className="section-divider" />

      {/* Workshop Section */}
      <div className="workshop-section">
        <h2 className="workshop-heading">{page.workshopTitle || "Workshop"}</h2>

        {page.hasWorkshop && page.workshopImage ? (
          <img
            src={page.workshopImage}
            alt="Workshop"
            className="workshop-image"
            loading="lazy"
          />
        ) : (
          <div className="workshop-placeholder">
            🎨 {page.workshopDescription || DEFAULT_PAGE.workshopDescription}
          </div>
        )}

        {hasCta ? (
          <a
            href={page.workshopCtaUrl}
            className="craft-button active"
            target="_blank"
            rel="noopener noreferrer"
          >
            {page.workshopCtaText || "Craft With Me"}
          </a>
        ) : (
          <button className="craft-button disabled" disabled>
            {page.workshopCtaText || "Craft With Me"}
          </button>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default HandmadeGifts;
